/**
 * state.ts - Global state management
 */

/**
 * UI state structure
 */
export interface UIState {
  activeTabStates: Record<string, string>;
  collapseStates: Record<string, boolean>;
  activeRoundStates: Record<string, number>;
}

// Track which matches sections are open
export const openMatchesSections = new Set<string>();

/**
 * Save current UI state before reloading
 * @returns State object containing active tabs, collapse states, and active rounds
 */
export function saveUIState(): UIState {
  const activeTabStates: Record<string, string> = {};
  const collapseStates: Record<string, boolean> = {};
  const activeRoundStates: Record<string, number> = {};

  // Save active tab for each tournament
  document.querySelectorAll<HTMLElement>('.main-tab-button.active').forEach(btn => {
    const tab = btn.dataset['tab'];
    const match = tab?.match(/^(\w+)-(.+)$/);
    if (match) {
      const [, tabName, tournamentId] = match;
      if (tournamentId && tabName) {
        activeTabStates[tournamentId] = tabName;
      }
    }
  });

  // Save collapse states
  document.querySelectorAll<HTMLElement>('[id^="tournament-content-"]').forEach(content => {
    const tournamentId = content.id.replace('tournament-content-', '');
    collapseStates[tournamentId] = content.style.display !== 'none';
  });

  // Save active round for each tournament
  document.querySelectorAll<HTMLElement>('.round-selector').forEach(selector => {
    const activeBtn = selector.querySelector<HTMLElement>('.btn-primary[onclick^="showMatchRound"]');
    if (activeBtn) {
      const match = activeBtn.getAttribute('onclick')?.match(/showMatchRound\('([^']+)',\s*(\d+)\)/);
      if (match) {
        const [, tournamentId, round] = match;
        if (tournamentId && round) {
          activeRoundStates[tournamentId] = parseInt(round);
        }
      }
    }
  });

  return { activeTabStates, collapseStates, activeRoundStates };
}

/**
 * Restore UI state after reloading
 * @param state - State object from saveUIState
 */
export function restoreUIState(state: UIState): void {
  const { activeTabStates, collapseStates, activeRoundStates } = state;

  setTimeout(() => {
    // Restore active tabs
    Object.entries(activeTabStates).forEach(([tournamentId, tabName]) => {
      if (window.switchMainTab) {
        window.switchMainTab(tournamentId, tabName);
      }
    });

    // Restore collapse states
    Object.entries(collapseStates).forEach(([tournamentId, isExpanded]) => {
      const content = document.getElementById(`tournament-content-${tournamentId}`);
      const icon = document.getElementById(`tournament-toggle-icon-${tournamentId}`);
      if (content && icon) {
        if (!isExpanded) {
          content.style.display = 'none';
          icon.textContent = '▶';
        }
      }
    });

    // Restore active rounds
    Object.entries(activeRoundStates).forEach(([tournamentId, round]) => {
      if (window.showMatchRound) {
        window.showMatchRound(tournamentId, round);
      }
    });
  }, 0);
}

// Export to window for global access

window.openMatchesSections = openMatchesSections;
window.saveUIState = saveUIState;
window.restoreUIState = restoreUIState;
